import type { UseBlotterTableProps } from '@talos/kyoko';
import { getCellDisplayValue } from '@talos/kyoko';
import type { GridApi, ProcessCellForExportParams, ShouldRowBeSkippedParams } from 'ag-grid-community';
import { CARE_ORDER_CUSTOM_COLUMN_IDS, type CareOrderBlotterColumnID } from './careOrderColumnIds';
import { isCareOrderExportMakerColumnId } from './columns/careOrderExportMakerColumns';
import { CareOrderRow, isQuoteRow, LegRow, type CareOrderBlotterEntity } from './types';

const CARE_ORDER_ONLY_COLUMN_IDS: Set<CareOrderBlotterColumnID> = new Set([
  'Counterparty',
  'initiatingFrom',
  'units',
  'fund',
  'Group',
  'Annotations.SettleOffset',
]);

// For flat export leg rows, only these columns are allowed to fallback to quote-row values.
const QUOTE_TO_LEG_FALLBACK_COLUMN_IDS: Set<CareOrderBlotterColumnID> = new Set([
  CARE_ORDER_CUSTOM_COLUMN_IDS.UnitsTraded,
  'User',
  CARE_ORDER_CUSTOM_COLUMN_IDS.SubAccount,
  'SettleDate',
]);

export function getCareOrderExportOptions({
  enableFlatCareOrderBlotterExport,
  enableCareOrderExportMakerColumns,
}: {
  enableFlatCareOrderBlotterExport: boolean;
  enableCareOrderExportMakerColumns: boolean;
}): UseBlotterTableProps<CareOrderBlotterEntity>['exportOptions'] {
  if (!enableFlatCareOrderBlotterExport) {
    return {
      name: 'Care Orders',
      baseParams: (api: GridApi | undefined) => ({
        skipRowGroups: false,
        ...getMakerColumnExportBaseParams({ api, enableCareOrderExportMakerColumns }),
        shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<CareOrderBlotterEntity>) =>
          params.node.data instanceof LegRow,
      }),
    };
  }

  return {
    name: 'Care Orders',
    baseParams: (api: GridApi | undefined) => ({
      shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<CareOrderBlotterEntity>) => {
        if (params.node.data instanceof CareOrderRow) {
          return true; // when flattening we always skip care order rows
        }

        if (params.node.data instanceof LegRow) {
          return false; // If there are leg rows, we will flatten down to the leg rows
        }

        if (isQuoteRow(params.node.data)) {
          // For quote rows, we remove them if they have LegRows as children. The Quote row's properties will be pushed onto the leg rows
          const quoteHasLegRows =
            params.node.childrenAfterGroup?.some(childNode => childNode.data instanceof LegRow) ?? false;
          return quoteHasLegRows;
        }

        return false; // N/A
      },
      processCellCallback: (params: ProcessCellForExportParams<CareOrderBlotterEntity>) => {
        const columnId = params.column.getColId() as CareOrderBlotterColumnID;
        const isCareOrderOnlyColumn = CARE_ORDER_ONLY_COLUMN_IDS.has(columnId);

        if (params.node?.data instanceof LegRow) {
          const isQuoteFallbackColumn = QUOTE_TO_LEG_FALLBACK_COLUMN_IDS.has(columnId);
          // Recall that if there are LegRows in the flattened view (which is where we are), then there will ONLY be leg rows
          // in the export. So below we try to resolve values both from the care order (our grandparent), and the quote row (parent)
          if (isCareOrderOnlyColumn) {
            const careOrderNode = params.node.parent?.parent;
            const careOrderData = careOrderNode?.data;
            if (!careOrderNode || !careOrderData) {
              return '';
            }

            const careOrderValue = params.api.getCellValue<unknown>({ colKey: params.column, rowNode: careOrderNode });
            return getCellDisplayValue({ ...params, node: careOrderNode, value: careOrderValue });
          }

          if (isQuoteFallbackColumn) {
            // If a leg value exists, always use that instead
            const legDisplayValue = getCellDisplayValue(params);
            if (!isEmptyCellDisplayValue(legDisplayValue)) {
              return legDisplayValue;
            }

            // Otherwise, try to grab the column value from our parent node (the quote row)
            const quoteNode = params.node.parent;
            const quoteData = quoteNode?.data;
            if (!quoteNode || !quoteData) {
              return '';
            }

            const quoteValue = params.api.getCellValue<unknown>({ colKey: params.column, rowNode: quoteNode });
            return getCellDisplayValue({ ...params, node: quoteNode, value: quoteValue });
          }

          return getCellDisplayValue(params);
        } else {
          // Else we are a Quote row
          if (isCareOrderOnlyColumn) {
            const parentNode = params.node?.parent;
            const parentData = parentNode?.data;
            if (!parentNode || !parentData) {
              return '';
            }

            const parentValue = params.api.getCellValue({ colKey: params.column, rowNode: parentNode });
            return getCellDisplayValue({ ...params, node: parentNode, value: parentValue });
          }

          return getCellDisplayValue(params);
        }
      },
      ...getMakerColumnExportBaseParams({ api, enableCareOrderExportMakerColumns }),
    }),
  };
}

/**
 * When maker export is enabled, export uses `includeHiddenColumns: true` so maker columns can be included in CSV.
 * Without this filter, every hidden blotter column would also be exported.
 * This keeps the export focused by allowing only hidden maker-export columns through.
 */
function getMakerColumnExportBaseParams({
  api,
  enableCareOrderExportMakerColumns,
}: {
  api: GridApi | undefined;
  enableCareOrderExportMakerColumns: boolean;
}) {
  if (!enableCareOrderExportMakerColumns) {
    return {};
  }

  const ignoredColIds = new Set<string>();
  const columnState = api?.getColumnState() ?? [];
  for (const column of columnState) {
    if (!column.hide) {
      continue;
    }
    if (!isCareOrderExportMakerColumnId(column.colId)) {
      ignoredColIds.add(column.colId);
    }
  }

  return {
    includeHiddenColumns: true,
    ignoredColIds,
  };
}

function isEmptyCellDisplayValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }

  return String(value).trim() === '';
}
